import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';
import toast from 'react-hot-toast';
import { sanitizeGame } from './game.slice';

const initialState = {
  library: [],
  cart: [],
  wishlist: [],
  wishlistDetailed: [],
  notifiedGames: [],
  recentlyViewed: [],
  recentlyViewedDetailed: [],
  isCartOpen: false,
  isWishlistOpen: false,
  lastUserPayload: null,
  lastFetched: null,
  loading: false,
  error: null,
};

// Async Thunk: Fetch User Profile Data (including cart and wishlist)
export const fetchUserData = createAsyncThunk(
  'userSlice/fetchUserData',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/users/${userId}`);
      if (res.data.success && res.data.user) {
        return {
          user: res.data.user,
          userId
        };
      }
      return rejectWithValue('User not found');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchUserLibrary = createAsyncThunk(
  'userSlice/fetchUserLibrary',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/users/${userId}/library`);
      if (res.data.success) {
        return res.data.library || [];
      }
      return rejectWithValue('Failed to fetch library');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Add to Cart with DB Sync (Instant non-blocking UI update)
export const addToCart = createAsyncThunk(
  'userSlice/addToCart',
  async (game, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { publicSettings } = state.gateway || {};

      if (publicSettings?.purchasePaused) {
        if (publicSettings.purchaseStartsAt) {
          const startsAt = new Date(publicSettings.purchaseStartsAt).getTime();
          if (startsAt > Date.now()) {
            toast.error("Purchases have not started yet!");
            return rejectWithValue("Purchases have not started yet");
          }
        } else {
          toast.error("Purchases are currently paused!");
          return rejectWithValue("Purchases are currently paused");
        }
      }

      const { isAuthenticated, user } = state.auth;
      const { cart } = state.userSlice;

      const targetId = game._id || game.id;
      const platform = game.selectedPlatform || 'windows';

      // Check if this game+platform combo already exists
      const existing = cart.find(
        item => (item.game._id || item.game.id) === targetId && item.platform === platform
      );

      if (existing) {
        toast.error(`${game.title} (${platform}) is already in your cart!`);
        return cart; // Return unchanged cart
      }

      const updatedCart = [...cart, {
        game: sanitizeGame({ ...game, id: targetId }),
        platform,
        quantity: 1,
        price: game.price,
        originalPrice: game.originalPrice,
        discount: game.discount
      }];

      // Fast background sync to MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        axiosInstance.patch(`/users/${userId}/cart`, { cart: updatedCart }).catch(err => {
          console.error("Cart background sync error:", err);
        });
      }

      toast.success(`${game.title} added to cart!`);
      return updatedCart;
    } catch (err) {
      console.error('Error adding to cart:', err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Remove from Cart with DB Sync (Instant non-blocking UI update)
export const removeFromCart = createAsyncThunk(
  'userSlice/removeFromCart',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { cart } = state.userSlice;

      let targetId;
      let platform;
      if (payload && typeof payload === 'object') {
        targetId = payload.gameId?._id || payload.gameId?.id || payload.gameId || payload.id || payload._id;
        platform = payload.platform;
      } else {
        targetId = payload;
      }

      const item = cart.find(i =>
        (i.game._id || i.game.id) === targetId &&
        (!platform || i.platform === platform)
      );

      const updatedCart = cart.filter(i =>
        !((i.game._id || i.game.id) === targetId && (!platform || i.platform === platform))
      );

      // Fast background sync to MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        axiosInstance.patch(`/users/${userId}/cart`, { cart: updatedCart }).catch(err => {
          console.error("Cart remove background sync error:", err);
        });
      }

      if (item?.game?.title) {
        toast.success(`${item.game.title}${platform ? ` (${platform})` : ''} removed from cart`);
      } else {
        toast.success(`Item removed from cart`);
      }

      return updatedCart;
    } catch (err) {
      console.error("Error removing from cart:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Update Quantity with DB Sync (Instant non-blocking UI update)
export const updateQuantity = createAsyncThunk(
  'userSlice/updateQuantity',
  async ({ gameId, quantity }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (quantity <= 0) {
        const actionResult = await dispatch(removeFromCart(gameId));
        return actionResult.payload;
      }

      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { cart } = state.userSlice;

      const updatedCart = cart.map(item =>
        (item.game._id || item.game.id) === gameId
          ? { ...item, quantity }
          : item
      );

      // Fast background sync to MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        axiosInstance.patch(`/users/${userId}/cart`, { cart: updatedCart }).catch(err => {
          console.error("Cart quantity background sync error:", err);
        });
      }

      return updatedCart;
    } catch (err) {
      console.error("Error updating quantity:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Toggle Wishlist with DB Sync (Instant non-blocking UI update)
export const toggleWishlist = createAsyncThunk(
  'userSlice/toggleWishlist',
  async (payload, { getState, rejectWithValue }) => {
    const state = getState();
    const { isAuthenticated, user } = state.auth;
    const { wishlist, wishlistDetailed } = state.userSlice;
    const { games = [], platformGames = [], searchGames = [], selectedGame = null, gameDetails = null } = state.game || {};
    const { trending = [], newRelease = [], bestSeller = [] } = state.home || {};

    let gameId;
    let directGameObj = null;
    if (payload && typeof payload === 'object') {
      gameId = payload._id || payload.id;
      directGameObj = payload;
    } else {
      gameId = payload;
    }

    const exists = wishlist.some(id => (id?._id || id?.id || id) === gameId);
    const updatedWishlist = exists
      ? wishlist.filter(id => (id?._id || id?.id || id) !== gameId)
      : [...wishlist, gameId];

    const gameObj = directGameObj ||
      (selectedGame && (selectedGame._id || selectedGame.id) === gameId ? selectedGame : null) ||
      (gameDetails && (gameDetails._id || gameDetails.id) === gameId ? gameDetails : null) ||
      games.find(g => (g._id || g.id) === gameId) ||
      wishlistDetailed.find(g => (g._id || g.id) === gameId) ||
      platformGames.find(g => (g._id || g.id) === gameId) ||
      (searchGames || []).find(g => (g._id || g.id) === gameId) ||
      trending.find(g => (g._id || g.id) === gameId) ||
      newRelease.find(g => (g._id || g.id) === gameId) ||
      bestSeller.find(g => (g._id || g.id) === gameId);

    const title = gameObj ? gameObj.title : "Game";

    let updatedWishlistDetailed = wishlistDetailed;
    if (exists) {
      updatedWishlistDetailed = wishlistDetailed.filter(g => (g._id || g.id) !== gameId);
      toast.success(`${title} removed from wishlist`);
    } else {
      if (gameObj && !wishlistDetailed.some(g => (g._id || g.id) === gameId)) {
        updatedWishlistDetailed = [...wishlistDetailed, sanitizeGame({ ...gameObj, id: gameObj._id || gameObj.id })];
      }
      toast.success(`${title} added to wishlist!`);
    }

    try {
      // Fast background sync to MongoDB if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        axiosInstance.patch(`/users/${userId}/wishlist`, { wishlist: updatedWishlist }).catch(err => {
          console.error("Wishlist background sync error:", err);
        });
      }

      return {
        wishlist: updatedWishlist,
        wishlistDetailed: updatedWishlistDetailed,
        exists,
        gameId
      };
    } catch (err) {
      console.error("Error toggling wishlist:", err);
      return rejectWithValue({
        previousWishlist: wishlist,
        previousWishlistDetailed: wishlistDetailed,
        message: err.message
      });
    }
  }
);

// Async Thunk: Toggle Upcoming Release Notifications with DB Sync
export const toggleUpcomingNotification = createAsyncThunk(
  'userSlice/toggleUpcomingNotification',
  async ({ gameId, gameTitle }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      const { notifiedGames } = state.userSlice;

      const exists = notifiedGames.includes(gameId);
      const updatedNotified = exists
        ? notifiedGames.filter(id => id !== gameId)
        : [...notifiedGames, gameId];

      // Sync with MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        await axiosInstance.patch(`/users/${userId}/upcoming-notifications`, { notifiedGames: updatedNotified });
      }

      if (exists) {
        toast.success(`You will no longer receive notifications for ${gameTitle || 'this game'}.`, {
          style: {
            background: '#070710',
            color: '#ff007a',
            border: '1px solid rgba(255, 0, 122, 0.2)',
            fontSize: '11px',
          }
        });
      } else {
        toast.success(`Notification set! We'll alert you when ${gameTitle || 'this game'} launches.`, {
          style: {
            background: '#070710',
            color: '#00f0ff',
            border: '1px solid rgba(0, 240, 255, 0.2)',
            fontSize: '11px',
          }
        });
      }

      return updatedNotified;
    } catch (err) {
      console.error("Error toggling upcoming notification:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Clear Cart with DB Sync (Instant non-blocking UI update)
export const clearCart = createAsyncThunk(
  'userSlice/clearCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;

      // Fast background sync to MongoDB database if authenticated
      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        axiosInstance.patch(`/users/${userId}/cart`, { cart: [] }).catch(err => {
          console.error("Clear cart background sync error:", err);
        });
      }

      return [];
    } catch (err) {
      console.error("Error clearing cart:", err);
      return rejectWithValue(err.message);
    }
  }
);

// Async Thunk: Sync Recently Viewed history with DB
export const syncRecentlyViewed = createAsyncThunk(
  'userSlice/syncRecentlyViewed',
  async (recentlyViewedIds, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { isAuthenticated, user } = state.auth;
      let dbRecent = null;

      if (isAuthenticated && user) {
        const userId = user._id || user.id;
        const res = await axiosInstance.patch(`/users/${userId}/recently-viewed`, { recentlyViewed: recentlyViewedIds });
        if (res.data.success && res.data.recentlyViewed) {
          dbRecent = res.data.recentlyViewed;
        }
      }

      return {
        recentlyViewed: recentlyViewedIds,
        recentlyViewedDetailed: dbRecent
      };
    } catch (err) {
      console.error("Error syncing recently viewed:", err);
      return rejectWithValue(err.message);
    }
  }
);

export const userSlice = createSlice({
  name: 'userSlice',
  initialState,
  reducers: {
    setIsCartOpen: (state, action) => {
      state.isCartOpen = action.payload;
      if (action.payload) state.isWishlistOpen = false;
    },
    setIsWishlistOpen: (state, action) => {
      state.isWishlistOpen = action.payload;
      if (action.payload) state.isCartOpen = false;
    },
    toggleCart: (state) => {
      state.isCartOpen = !state.isCartOpen;
      if (state.isCartOpen) state.isWishlistOpen = false;
    },
    toggleWishlistDrawer: (state) => {
      state.isWishlistOpen = !state.isWishlistOpen;
      if (state.isWishlistOpen) state.isCartOpen = false;
    },
    setLibrary: (state, action) => {
      state.library = action.payload;
    },
    resetUserSessionData: (state) => {
      state.cart = [];
      state.wishlist = [];
      state.wishlistDetailed = [];
      state.library = [];
      state.notifiedGames = [];
      state.recentlyViewed = [];
      state.recentlyViewedDetailed = [];
      state.lastUserPayload = null;
      state.lastFetched = null;
      try {
        localStorage.removeItem('cubegames_recent');
      } catch (e) {
        console.error('localStorage access failed:', e);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserData
      .addCase(fetchUserData.fulfilled, (state, action) => {
        const dbUser = action.payload.user;
        state.lastUserPayload = action.payload.userId;
        state.lastFetched = Date.now();

        // 1. Cart merging (preserve guest cart items and merge with DB cart)
        const serverCart = (dbUser.cart || []).map(item => ({
          game: sanitizeGame({
            ...item.game,
            id: item.game?._id || item.game?.id
          }),
          platform: item.platform || 'windows',
          quantity: item.quantity || 1,
          price: item.price,
          originalPrice: item.originalPrice,
          discount: item.discount
        })).filter(item => item.game && item.game.id);

        const mergedCart = [...(state.cart || [])];
        let hasNewCartItems = false;

        serverCart.forEach(serverItem => {
          const exists = mergedCart.some(localItem =>
            (localItem.game?._id || localItem.game?.id) === (serverItem.game?._id || serverItem.game?.id) &&
            localItem.platform === serverItem.platform
          );
          if (!exists) {
            mergedCart.push(serverItem);
          }
        });

        if (mergedCart.length > serverCart.length) {
          hasNewCartItems = true;
        }

        state.cart = mergedCart;

        if (hasNewCartItems && action.payload.userId) {
          axiosInstance.patch(`/users/${action.payload.userId}/cart`, { cart: mergedCart }).catch(err => {
            console.error("Cart sync on login error:", err);
          });
        }

        // 2. Wishlist merging (preserve guest wishlist items and merge with DB wishlist in order)
        const serverWishlistIds = (dbUser.wishlist || []).map(g => g._id || g.id || g);
        const serverWishlistDetailed = (dbUser.wishlist || []).map(g => sanitizeGame({
          ...g,
          id: g._id || g.id
        })).filter(g => g && g.id);

        const currentWishlist = state.wishlist || [];
        const currentWishlistDetailed = state.wishlistDetailed || [];

        // Keep existing guest wishlist items first, then append any server wishlist items that are not in guest wishlist
        const mergedWishlistIds = [...currentWishlist];
        let hasNewWishlistItems = false;

        serverWishlistIds.forEach(id => {
          const rawId = id?._id || id?.id || id;
          if (!mergedWishlistIds.some(existing => (existing?._id || existing?.id || existing) === rawId)) {
            mergedWishlistIds.push(rawId);
          }
        });

        const mergedWishlistDetailed = [...currentWishlistDetailed];
        serverWishlistDetailed.forEach(item => {
          const itemId = item._id || item.id;
          if (!mergedWishlistDetailed.some(existing => (existing._id || existing.id) === itemId)) {
            mergedWishlistDetailed.push(item);
          }
        });

        if (mergedWishlistIds.length > serverWishlistIds.length) {
          hasNewWishlistItems = true;
        }

        state.wishlist = mergedWishlistIds;
        state.wishlistDetailed = mergedWishlistDetailed;

        // If guest added wishlist items before logging in, sync the merged wishlist to DB
        if (hasNewWishlistItems && action.payload.userId) {
          axiosInstance.patch(`/users/${action.payload.userId}/wishlist`, { wishlist: mergedWishlistIds }).catch(err => {
            console.error("Wishlist sync on login error:", err);
          });
        }

        if (dbUser.library) {
          state.library = dbUser.library.map(g => g._id || g.id || g);
        }

        if (dbUser.notifiedGames) {
          state.notifiedGames = dbUser.notifiedGames.map(g => g._id || g.id || g);
        }

        if (dbUser.recentlyViewed) {
          state.recentlyViewed = dbUser.recentlyViewed.map(g => g._id || g.id || g);
          state.recentlyViewedDetailed = dbUser.recentlyViewed.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })).filter(g => g && g.id);
        }
      })
      .addCase('auth/logout/fulfilled', (state) => {
        state.cart = [];
        state.wishlist = [];
        state.wishlistDetailed = [];
        state.library = [];
        state.notifiedGames = [];
        state.recentlyViewed = [];
        state.recentlyViewedDetailed = [];
        state.lastUserPayload = null;
        state.lastFetched = null;
      })

      // addToCart
      .addCase(addToCart.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      // removeFromCart
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      // updateQuantity
      .addCase(updateQuantity.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      // toggleWishlist
      .addCase(toggleWishlist.fulfilled, (state, action) => {
        state.wishlist = action.payload.wishlist;
        if (action.payload.wishlistDetailed) {
          state.wishlistDetailed = action.payload.wishlistDetailed.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })).filter(g => g && g.id);
        } else {
          // Guest mode or fallback logic
          if (action.payload.exists) {
            state.wishlistDetailed = state.wishlistDetailed.filter(g => (g.id || g._id) !== action.payload.gameId);
          } else if (action.payload.toggledGame) {
            if (!state.wishlistDetailed.some(g => (g.id || g._id) === action.payload.gameId)) {
              state.wishlistDetailed = [...state.wishlistDetailed, action.payload.toggledGame];
            }
          }
        }
      })
      .addCase(toggleWishlist.rejected, (state, action) => {
        if (action.payload?.previousWishlist) {
          state.wishlist = action.payload.previousWishlist;
        }
        if (action.payload?.previousWishlistDetailed) {
          state.wishlistDetailed = action.payload.previousWishlistDetailed;
        }
      })

      // toggleUpcomingNotification
      .addCase(toggleUpcomingNotification.fulfilled, (state, action) => {
        state.notifiedGames = action.payload;
      })

      // clearCart
      .addCase(clearCart.fulfilled, (state, action) => {
        state.cart = action.payload;
      })

      .addCase(fetchUserLibrary.fulfilled, (state, action) => {
        state.library = (action.payload || []).map((g) => g._id || g.id || g);
      })
      
      .addCase(syncRecentlyViewed.fulfilled, (state, action) => {
        state.recentlyViewed = action.payload.recentlyViewed;
        if (action.payload.recentlyViewedDetailed) {
          state.recentlyViewedDetailed = action.payload.recentlyViewedDetailed.map(g => sanitizeGame({
            ...g,
            id: g._id || g.id
          })).filter(g => g && g.id);
        }
      });
  }
});

export const {
  setIsCartOpen,
  setIsWishlistOpen,
  toggleCart,
  toggleWishlistDrawer,
  setLibrary,
  resetUserSessionData
} = userSlice.actions;

export default userSlice.reducer;
